import classNames from "classnames";
import { ArrowDown } from "components/icons/ArrowDown";
import { useState } from "react";
import { EntityReviewItem } from "v2/store/onboardingEntityApi";
import { joinLegalName } from "v2/utils/joinLegalName";
import { useEntityReviewAutosave } from "v2/utils/useEntityReviewAutosave";
import { EntityFlagAvatar } from "../../EntityFlagAvatar";
import { EntityLocationSection } from "./EntityLocationSection";
import { PeopleSection } from "./PeopleSection";

type EntityReviewAccordionProps = {
  item: EntityReviewItem;
  groupId: string;
  countryCode?: string;
  defaultOpen?: boolean;
  showErrors?: boolean;
};

export const EntityReviewAccordion = ({
  item,
  groupId,
  countryCode,
  defaultOpen = false,
  showErrors = false,
}: EntityReviewAccordionProps) => {
  const [open, setOpen] = useState(defaultOpen);

  const save = useEntityReviewAutosave(groupId, item.entityId);

  const hasMissingMandatory =
    !item.dateOfIncorporation || !item.states.some((s) => s.isHome);
  const isOpen = open || (showErrors && hasMissingMandatory);

  const legalName =
    item.registeredName ||
    joinLegalName(item.entityName, item.entitySuffix) ||
    "Entity";

  const handleSetHomeState = (id: string, name: string) => {
    const withoutHome = item.states.filter((s) => !s.isHome);
    const isExistingOther = withoutHome.some((s) => s.id === id);
    const nextStates = isExistingOther
      ? withoutHome.map((s) => ({ ...s, isHome: s.id === id }))
      : [...withoutHome, { id, name, isHome: true }];

    save({ states: nextStates });
  };

  const handleAddState = (id: string, name: string) =>
    save({ states: [...item.states, { id, name, isHome: false }] });

  const handleRemoveState = (id: string) =>
    save({ states: item.states.filter((s) => s.id !== id) });

  const handleEinChange = (value: string) => save({ ein: value });

  const handleDoiChange = (value: string) =>
    save({ dateOfIncorporation: value });

  const handleToggle = () => setOpen((prev) => !prev);

  return (
    <div className="t-flex t-flex-col">
      <button
        type="button"
        onClick={handleToggle}
        className="all:unset t-flex t-w-full t-cursor-pointer t-items-center t-gap-3 t-text-left"
      >
        <span
          className={classNames(
            "t-flex t-shrink-0 t-text-text-30 t-transition-transform t-duration-200",
            { "-t-rotate-90": !isOpen }
          )}
        >
          <ArrowDown strokeWidth="1" size="16" />
        </span>
        <EntityFlagAvatar countryCode={countryCode} size="sm" />
        <span className="t-flex t-flex-col">
          <span className="t-text-body t-font-medium t-text-text-60">
            {legalName}
          </span>
          <span className="t-text-body t-text-neutral-40">
            {item.entityTypeLabel}
          </span>
        </span>
      </button>

      {isOpen && (
        <div className="t-flex t-flex-col t-gap-5 t-pl-7 t-pt-4">
          <EntityLocationSection
            entityId={item.entityId}
            ein={item.ein}
            dateOfIncorporation={item.dateOfIncorporation}
            states={item.states}
            showErrors={showErrors}
            onEinChange={handleEinChange}
            onDoiChange={handleDoiChange}
            onSetHomeState={handleSetHomeState}
            onAddState={handleAddState}
            onRemoveState={handleRemoveState}
          />

          <div className="t-h-px t-w-full t-bg-neutral-0" />

          <div>
            <div className="t-text-subtext t-text-text-60">
              People and roles
            </div>
            <div className="t-mb-4 t-text-body-sm t-text-neutral-40">
              Directors, officers, shareholders, and other connected people
            </div>
            <PeopleSection
              people={item.people}
              entityId={item.entityId}
              groupId={groupId}
            />
          </div>
        </div>
      )}
    </div>
  );
};
